import { EventDetail } from "@/mocks/eventDetails";

interface Props {
  event: EventDetail;
}

export default function EventContent({ event }: Props) {
  return (
    <div className="flex flex-col gap-10">
      {/* Description */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-file-text-line text-red-500" />
          活動介紹
        </h2>
        <div className="text-gray-600 text-base leading-relaxed whitespace-pre-line">
          {event.description}
        </div>
      </div>

      {/* Highlights */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-star-line text-red-500" />
          活動亮點
        </h2>
        <ul className="flex flex-col gap-3">
          {event.highlights.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-red-100 shrink-0 mt-0.5">
                <i className="ri-check-line text-red-600 text-xs" />
              </div>
              <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Schedule */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-time-line text-red-500" />
          活動流程
        </h2>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-red-100" />
          <div className="flex flex-col gap-0">
            {event.schedule.map((item, idx) => (
              <div key={idx} className="flex gap-4 relative pb-6 last:pb-0">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold shrink-0 z-10">
                  {idx + 1}
                </div>
                <div className="pt-1.5 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-red-600 text-xs font-semibold bg-red-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                      {item.time}
                    </span>
                    <span className="text-gray-900 font-bold text-sm">{item.title}</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Includes */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-gift-line text-red-500" />
          費用包含
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {event.includes.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2.5 bg-stone-50 rounded-xl px-4 py-3">
              <i className="ri-checkbox-circle-fill text-red-500 text-base shrink-0" />
              <span className="text-gray-700 text-sm">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Target Audience */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-user-heart-line text-red-500" />
          適合對象
        </h2>
        <div className="flex flex-col gap-2">
          {event.targetAudience.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-red-400 shrink-0" />
              <span className="text-gray-600 text-sm">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-image-line text-red-500" />
          活動照片
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {event.galleryImages.map((img, idx) => (
            <div key={idx} className="rounded-xl overflow-hidden" style={{ height: "180px" }}>
              <img
                src={img}
                alt={`活動照片 ${idx + 1}`}
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Map */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
          <i className="ri-map-2-line text-red-500" />
          活動地點
        </h2>
        <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: "280px" }}>
          <iframe
            src={event.locationMapUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`${event.title} 地圖`}
          />
        </div>
        <p className="text-gray-500 text-sm mt-2 flex items-center gap-1.5">
          <i className="ri-map-pin-line text-red-500" />
          {event.location}
        </p>
      </div>
    </div>
  );
}
